import { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import RiskBadge from "@/components/RiskBadge";
import { Phone, Search as SearchIcon, EyeOff } from "lucide-react";

export default function PhoneLookup() {
  const [params, setParams] = useSearchParams();
  const [phone, setPhone] = useState(params.get("q") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const doLookup = useCallback(async (val) => {
    if (!val) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/lookup/phone/${encodeURIComponent(val)}`);
      setResult(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const initial = params.get("q");
    if (initial) doLookup(initial);
  }, [params, doLookup]);

  const onSubmit = (e) => {
    e.preventDefault();
    setParams({ q: phone });
    doLookup(phone);
  };

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="phone-lookup-page">
      <header>
        <div className="text-xs font-mono uppercase tracking-wider text-blue-700">
          Lookup
        </div>
        <h1 className="font-display font-bold tracking-tight text-3xl">
          Phone number lookup
        </h1>
        <p className="text-slate-500 text-sm mt-2 max-w-xl">
          Enter a phone (any format) to find every advertisement linked to it.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="flex gap-3 max-w-2xl border border-slate-200 bg-white rounded-md p-3"
      >
        <div className="relative flex-1">
          <Phone className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 98450 11220"
            className="pl-9 font-mono"
          />
        </div>
        <Button
          type="submit"
          data-testid="phone-submit"
          className="bg-slate-900 hover:bg-slate-800"
        >
          <SearchIcon className="h-4 w-4 mr-2" /> Search
        </Button>
      </form>

      {loading && <div className="text-sm text-slate-500">Searching…</div>}

      {result && (
        <div className="space-y-4">
          <div className="text-sm text-slate-600">
            <span className="font-mono text-slate-900">{result.phone}</span>
            <span className="ml-2 text-slate-500">
              · {result.match_count} matching ad{result.match_count === 1 ? "" : "s"}
            </span>
          </div>

          <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
            {(result.ads || []).map((a) => (
              <Link
                key={a.id}
                to={`/ads/${a.id}`}
                data-testid={`phone-result-${a.id}`}
                className="block px-4 py-3 border-b border-slate-100 hover:bg-slate-50"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{a.title}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {a.locality || "Unknown"} · {(a.text || "").slice(0, 100)}…
                    </div>
                  </div>
                  <RiskBadge level={a.risk.level} score={a.risk.score} />
                </div>
              </Link>
            ))}
            {(result.ads || []).length === 0 && (
              <div className="px-4 py-6 text-sm text-slate-500">
                No advertisements found for this phone number.
              </div>
            )}
          </div>

          {(result.possible_matches || []).length > 0 && (
            <div className="space-y-2" data-testid="possible-matches-section">
              <div className="flex items-center gap-2 text-sm text-amber-800">
                <EyeOff className="h-4 w-4" />
                <span className="font-medium">
                  {result.possible_matches.length} possible obfuscated match{result.possible_matches.length === 1 ? "" : "es"}
                </span>
                <span className="text-xs text-slate-500">— masked numbers consistent with this phone (lead only, not proof)</span>
              </div>
              <div className="border border-amber-200 bg-amber-50/40 rounded-md overflow-hidden">
                {result.possible_matches.map((a) => (
                  <Link
                    key={a.id}
                    to={`/ads/${a.id}`}
                    data-testid={`possible-match-${a.id}`}
                    className="block px-4 py-3 border-b border-amber-100 hover:bg-amber-50"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{a.title}</div>
                        <div className="text-xs text-slate-500 truncate">
                          masked as <span className="font-mono text-amber-800">{(a.matched_patterns || []).join(", ")}</span>
                          {a.locality ? ` · ${a.locality}` : ""}
                        </div>
                      </div>
                      <RiskBadge level={a.risk.level} score={a.risk.score} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
